import React, { useEffect, useState } from 'react';
import { useRecordContext, useDataProvider, useNotify } from 'react-admin';
import { Box, Chip, CircularProgress, Typography, Alert, Divider } from '@mui/material';
import { CheckCircle, Cancel, Info } from '@mui/icons-material';
import { codesFields } from '../../codes.model';

/**
 * CodeProductFeatures Component
 * 
 * Displays product features enabled for a specific organization code.
 * Shows both code-specific features and inherited org-level features.
 * 
 * Gated by: enableAdminOrganizationFeatureToggles feature flag
 */
const CodeProductFeatures = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    
    const [loading, setLoading] = useState(true);
    const [codeFeatures, setCodeFeatures] = useState({});
    const [effectiveFeatures, setEffectiveFeatures] = useState({});
    const [allFeatures, setAllFeatures] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (record) {
            loadFeatures();
        }
    }, [record]);

    const loadFeatures = async () => {
        if (!record[codesFields.organizationId] || !record[codesFields.id]) {
            setError('Missing organization or code ID');
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Fetch all available features (for display names)
            const allFeaturesRes = await dataProvider.getList('product-features', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'display_name', order: 'ASC' }
            });

            // Fetch features for this specific code
            const response = await fetch(
                `/api/organizations/${record[codesFields.organizationId]}/codes/${record[codesFields.id]}/product_features`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch features: ${response.status}`);
            }

            const data = await response.json();
            
            setAllFeatures(allFeaturesRes.data);
            setCodeFeatures(data.code_features || {});
            setEffectiveFeatures(data.effective_features || {});
            setError(null);
        } catch (err) {
            console.error('Error loading code features:', err);
            setError(err.message);
            notify('Error loading product features', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading features...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 1 }}>
                {error}
            </Alert>
        );
    }

    const featuresMap = {};
    allFeatures.forEach(f => {
        featuresMap[f.name] = f.display_name;
    });

    const enabledCodeFeatures = Object.entries(codeFeatures)
        .filter(([_, enabled]) => enabled)
        .map(([name, _]) => name);

    const enabledEffectiveFeatures = Object.entries(effectiveFeatures)
        .filter(([_, enabled]) => enabled)
        .map(([name, _]) => name);

    const inheritedFeatures = enabledEffectiveFeatures.filter(
        name => !codeFeatures[name]
    );

    if (enabledEffectiveFeatures.length === 0) {
        return (
            <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    <Cancel fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    No product features enabled for this code
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 1 }}>
            {/* Code-Specific Features */}
            {enabledCodeFeatures.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        <CheckCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: 'success.main' }} />
                        Code-Specific Features ({enabledCodeFeatures.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {enabledCodeFeatures.map(featureName => (
                            <Chip
                                key={featureName}
                                label={featuresMap[featureName] || featureName}
                                size="small"
                                color="primary"
                                icon={<CheckCircle />}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Inherited from Organization */}
            {inheritedFeatures.length > 0 && (
                <>
                    {enabledCodeFeatures.length > 0 && <Divider sx={{ my: 1 }} />}
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            <Info fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: 'info.main' }} />
                            Inherited from Organization ({inheritedFeatures.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {inheritedFeatures.map(featureName => (
                                <Chip
                                    key={featureName}
                                    label={featuresMap[featureName] || featureName}
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                    icon={<Info />}
                                />
                            ))}
                        </Box>
                    </Box>
                </>
            )}

            {/* Summary */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Total: {enabledEffectiveFeatures.length} feature{enabledEffectiveFeatures.length !== 1 ? 's' : ''} enabled
                {enabledCodeFeatures.length > 0 && inheritedFeatures.length > 0 && 
                    ` (${enabledCodeFeatures.length} code-specific + ${inheritedFeatures.length} inherited)`
                }
            </Typography>
        </Box>
    );
};

export default CodeProductFeatures;

